import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/SupabaseClient";
import FaceLoginCamera from "../components/FaceLoginCamera";

const apiBase = import.meta.env.VITE_API_BASE;

export default function Login() {
    const nav = useNavigate();
    const { setUserAndRole } = useAuth();

    const [email, setEmail] = useState("info@secureid.com");
    const [password, setPassword] = useState("Malaver2025!");
    const [showPassword, setShowPassword] = useState(false);
    const [remember, setRemember] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const [method, setMethod] = useState<"none" | "password" | "otp" | "face" | "voice">("none");
    const [cameraEnabled, setCameraEnabled] = useState(false);
    const [voiceEnabled, setVoiceEnabled] = useState(false);
    const [message, setMessage] = useState("");

    /** ✅ Password login (your existing logic) */
    async function handlePasswordLogin(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        setBusy(true);
        try {
            const res = await fetch(`${apiBase}/api/user/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password }),
            });

            if (!res.ok) {
                const msg = await res.text();
                throw new Error(msg || "Login failed");
            }

            const data = await res.json();
            if (remember) localStorage.setItem("token", data.token);
            else sessionStorage.setItem("token", data.token);

            setUserAndRole(data.user, data.role, null, data.token, remember ? "local" : "session");
            nav("/dashboard");
        } catch (err: any) {
            console.error("❌ Login error:", err);
            setError(err.message);
        } finally {
            setBusy(false);
        }
    }

    /** 📩 6-digit PIN (Supabase OTP) */
    async function requestPin() {
        try {
            setBusy(true);
            setMessage("");

            const { error } = await supabase.auth.signInWithOtp({
                email,
                options: { shouldCreateUser: false },
            });
            if (error) throw error;

            setMessage("📧 A 6-digit verification code has been sent to your email.");
            localStorage.setItem("adminEmail", email);
            setTimeout(() => nav("/candidate/verify"), 1200);
        } catch (err: any) {
            console.error("PIN error:", err);
            setMessage("❌ " + err.message);
        } finally {
            setBusy(false);
        }
    }

    /** 📸 Face recognition */
    const handleFaceCapture = async (blob: Blob) => {
        try {
            const formData = new FormData();
            formData.append("Image", blob, "face.jpg");

            const res = await fetch(`${apiBase}/api/face/match?email=${encodeURIComponent(email)}`, {
                method: "POST",
                body: formData,
            });

            const data = await res.json();
            if (data.match) {
                setMessage("✅ Face verified! Redirecting...");
                setTimeout(() => nav("/dashboard"), 1000);
            } else {
                setMessage("❌ Face not recognized. Try another method.");
            }
        } catch (err) {
            console.error("Face verification error:", err);
            setMessage("⚠️ Face verification failed.");
        }
    };

    /** 🎤 Voice recognition (placeholder) */
    const handleVoiceLogin = async () => {
        setVoiceEnabled(true);
        setMessage("🎤 Voice recognition is under development.");
    };

    return (
        <div className="min-h-screen flex flex-col justify-center items-center bg-gradient-to-b from-indigo-700 via-indigo-600 to-violet-600 px-4">
            <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-8">
                <div className="text-center mb-6">
                    <img
                        src="/Images/ExamsPro.png"
                        alt="ExamsPro Logo"
                        className="w-14 h-14 mx-auto mb-2"
                    />
                    <h2 className="text-2xl font-bold text-indigo-700">Exams Admin Login</h2>
                    <p className="text-gray-500 text-sm mt-1">SecureID Technologies – ExamsPro</p>
                </div>

                {/* Email input */}
                <div className="space-y-4 mb-6">
                    <input
                        type="email"
                        placeholder="Email address"
                        className="w-full border border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-200 px-3 py-2 rounded-md outline-none transition"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </div>

                {/* Method selector */}
                {method === "none" && (
                    <div className="space-y-4 text-center">
                        <button
                            onClick={() => {
                                setMethod("otp");
                                requestPin();
                            }}
                            disabled={busy}
                            className="w-full bg-indigo-600 text-white py-2 rounded-md font-medium shadow hover:bg-indigo-700 transition"
                        >
                            {busy ? "Sending PIN..." : "Request 6-Digit PIN"}
                        </button>

                        <button
                            onClick={() => setMethod("face")}
                            className="w-full bg-violet-600 text-white py-2 rounded-md font-medium shadow hover:bg-violet-700 transition"
                        >
                            Use Face Recognition
                        </button>

                        <button
                            onClick={() => handleVoiceLogin()}
                            className="w-full bg-pink-600 text-white py-2 rounded-md font-medium shadow hover:bg-pink-700 transition"
                        >
                            Use Voice Recognition
                        </button>

                        <button
                            onClick={() => setMethod("password")}
                            className="w-full border border-indigo-500 text-indigo-700 py-2 rounded-md font-medium hover:bg-indigo-50 transition"
                        >
                            Use Password
                        </button>
                    </div>
                )}

                {/* Password login form */}
                {method === "password" && (
                    <form onSubmit={handlePasswordLogin} className="space-y-4 mt-6">
                        <div className="relative">
                            <input
                                className="w-full border border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-200 px-3 py-2 rounded-md outline-none transition"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Password"
                                required
                            />
                            <button
                                type="button"
                                className="absolute right-3 top-2 text-sm text-gray-500 hover:text-indigo-600"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? "Hide" : "Show"}
                            </button>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={remember}
                                    onChange={(e) => setRemember(e.target.checked)}
                                />
                                Remember me
                            </label>
                            <Link to="/forgot-password" className="text-indigo-600 hover:underline">
                                Forgot password?
                            </Link>
                        </div>

                        <button
                            className="w-full bg-indigo-600 text-white py-2 rounded-md font-medium shadow hover:bg-indigo-700 transition"
                            disabled={busy}
                        >
                            {busy ? "Please wait…" : "Sign in"}
                        </button>

                        <button
                            type="button"
                            onClick={() => setMethod("none")}
                            className="block mx-auto mt-2 text-sm text-indigo-600 hover:underline"
                        >
                            ← Back
                        </button>
                    </form>
                )}

                {/* Face recognition mode */}
                {method === "face" && !cameraEnabled && (
                    <div className="text-center mt-6 space-y-3">
                        <button
                            onClick={() => setCameraEnabled(true)}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700 transition"
                        >
                            Enable Camera
                        </button>
                        <p className="text-sm text-gray-600">
                            Tap “Enable Camera” to start face verification.
                        </p>
                        <button
                            onClick={() => setMethod("none")}
                            className="text-sm text-indigo-600 hover:underline"
                        >
                            ← Back
                        </button>
                    </div>
                )}

                {cameraEnabled && <FaceLoginCamera onCapture={handleFaceCapture} />}

                {/* Voice recognition placeholder */}
                {voiceEnabled && (
                    <div className="mt-6 text-center text-sm text-gray-600">
                        🎙️ Listening for your voice sample...
                        <br />
                        <button
                            onClick={() => setVoiceEnabled(false)}
                            className="mt-2 text-indigo-600 hover:underline"
                        >
                            Cancel
                        </button>
                    </div>
                )}

                {message && (
                    <p className="mt-5 text-sm text-center text-gray-700 whitespace-pre-line">{message}</p>
                )}

                {error && (
                    <div className="mt-4 text-sm text-center text-red-600 font-medium">{error}</div>
                )}

                <div className="mt-8 text-center">
                    <Link
                        to="/solutions/examspro"
                        className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-indigo-600 transition"
                    >
                        <span className="text-lg leading-none">←</span>
                        <span>Back to ExamsPro</span>
                    </Link>
                </div>
            </div>
        </div>
    );
}
